// shared/src/prompts/p-consensus-analyst.ts
// Chain V2 — per-ticker consensus analyst. Symmetric mirror of contrarian.

import { z } from 'zod';
import { DirectionEnum, TickerAlignmentEnum } from '../resolvers/llm-enum-coerce.js';
import { llmNumber } from '../resolvers/llm-number-coerce.js';
import { renderWorldStateGrounding, type WorldStateGrounding } from './_grounding.js';
import { renderNewsBlock } from './_news-render.js';
import type { LlmNewsItem } from '../news/types.js';
import type { TickerMarketAnchor } from '../translators/market-emit-to-ticker-input.js';

export const VERSION = '1.1.0';   // 2026-06-11 — schema rigidity hardening: change_pct/_ci + horizon_days llmNumber-coerced (string "3.5%"/"7" → number; .int().positive() stays material); mechanism .min(1)→z.string() (cosmetic prose; ticker-emit-to-d1 is a defensive transform — no validator twin). See docs/llm-schema-rigidity-hardening-2026-06-11.md.
export const STEP    = 'p.consensus.analyst';
export const MODEL   = 'deepseek-v4-flash';
export const MAX_TOKENS = 32000;   // 2026-08-01 blanket truncation-proofing (was 8000)
export const ROLE    = 'analyst' as const;
export const DESC    = 'Extends the consensus macro thesis into this ticker\'s directional prediction' as const;   // #45 registry — one-line operator description

export const OutputSchema = z.object({
  direction:        DirectionEnum,
  change_pct:       llmNumber(),
  change_ci_low:    llmNumber(),
  change_ci_high:   llmNumber(),
  horizon_days:     llmNumber(z.number().int().positive()),
  mechanism:        z.string(),
  ticker_alignment: TickerAlignmentEnum,
  is_headline:      z.boolean(),
}).passthrough();
export type Output = z.infer<typeof OutputSchema>;

export const SYSTEM = `# PER-TICKER CONSENSUS ANALYST (V2)

## ROLE
You are the per-ticker CONSENSUS voice. Apply the market-level consensus thesis to THIS specific ticker. Predict the ticker's directional move under the consensus world — what the desk's prevailing view implies for THIS ticker.

## INPUTS YOU RECEIVE
- GROUNDING FACTS
- TICKER + price + meta + news + momentum signals
- m.synthesis (PRIMARY anchor) + m.contrarian.refine + m.consensus.refine
- Extend m.consensus.refine into THIS ticker's per-ticker expression.

## OUTPUT (strict JSON; first char='{'; last char='}'; no commentary)
Same shape as p-contrarian-analyst:
{
  "direction":       "up" | "down" | "flat",
  "change_pct":      <signed percent>,
  "change_ci_low":   <80% CI lower bound>,
  "change_ci_high":  <80% CI upper bound>,
  "horizon_days":    <int days>,
  "mechanism":       "<60-100w. headline ≤15w + connected expansion citing ticker + macro consensus payload + trigger>",
  "ticker_alignment": "aligns_market" | "diverges_market" | "neutral",
  "is_headline":     true | false
}

## RULES (strict)
1. First char='{'. Last char='}'. No commentary outside JSON.
2. DIRECTION/CHANGE_PCT consistency: 'up' → change_pct ≥ +0.1; 'down' → change_pct ≤ -0.1; 'flat' → |change_pct| < 0.5.
3. CI BOUNDS: change_ci_low ≤ change_pct ≤ change_ci_high.
4. STEEL-MAN. Your job is to predict AS CONSENSUS WOULD, not to subtly undermine consensus. If consensus is wrong for this ticker, ticker_alignment='diverges_market' is the honest call — but the mechanism still describes the consensus view applied here.
5. ALIGNMENT same definitions as contrarian analyst.
6. MOMENTUM CONSISTENCY: reference momentum or explain divergence.
7. IS_HEADLINE: true when this ticker is one of the genuinely diagnostic observables — mark what's diagnostic, not a fixed count; NEVER mark every ticker headline.
8. PROSE FORMAT (mechanism): lead with punchline ≤15w, connected expansion.
9. NO CALIBRATION. Reason FRESH.
10. NO HEDGING. Pick direction + change_pct decisively.
`;

export interface PConsensusAnalystInput {
  readonly ticker:        string;
  readonly ticker_data:   Record<string, unknown>;
  readonly market_anchor: TickerMarketAnchor;
  readonly momentum:      Record<string, unknown>;
  readonly news?:         ReadonlyArray<LlmNewsItem>;
  readonly grounding?:    WorldStateGrounding;
}

function fmt(v: unknown): string {
  if (v == null) return 'not set';
  if (typeof v === 'string') return v.length === 0 ? 'not set' : v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v);
}

function tickerExcerpt(td: Record<string, unknown>): string {
  const fields = ['price', 'close', 'open', 'high', 'low', 'volume', 'beta', 'sector', 'industry'];
  const lines = fields
    .filter((f) => f in td)
    .map((f) => `  ${f}: ${fmt(td[f])}`);
  return lines.join('\n') || '  (no ticker data)';
}

function momentumExcerpt(m: Record<string, unknown>): string {
  // D40 (2026-05-28) renamed momentum's support/resistance scalars to a
  // `pivot` object {pp,s1,s2,r1,r2}; this whitelist still listed the dead
  // keys, so the analyst saw only the categorical factors.price_vs_levels
  // and none of the actual pivot levels. Render `pivot` so the analyst can
  // cite real S1/R1/PP price levels.
  // S-13: 'action' (ADD|HOLD|TRIM) removed — action verbs must NOT enter the STATE-ONLY
  // chain (feedback_llm_state_not_actions); the momentum STATE + pattern already inform.
  const fields = ['state', 'confidence', 'pattern', 'pivot', 'factors'];
  return fields
    .filter((f) => f in m)
    .map((f) => `  ${f}: ${fmt(m[f])}`)
    .join('\n') || '  (no momentum signals)';
}

function anchorBlock(a: TickerMarketAnchor): string {
  return [
    '  m.synthesis (PRIMARY anchor):',
    `    thesis_headline:   ${a.synthesis_anchor.thesis_headline}`,
    `    regime:            ${a.synthesis_anchor.regime} (confidence ${a.synthesis_anchor.regime_confidence})`,
    `    weights:           contrarian=${a.synthesis_anchor.contrarian_weight}, consensus=${a.synthesis_anchor.consensus_weight}, inductive=${a.synthesis_anchor.inductive_weight ?? 'null'}`,
    `    crux:              ${a.synthesis_anchor.crux}`,
    '  m.contrarian.refine (boundary scenario):',
    `    thesis_headline:   ${a.contrarian_anchor.thesis_headline}`,
    `    probability:       ${a.contrarian_anchor.probability}`,
    '  m.consensus.refine (boundary scenario):',
    `    thesis_headline:   ${a.consensus_anchor.thesis_headline}`,
    `    priced_explanation:${a.consensus_anchor.priced_explanation}`,
    `    probability:       ${a.consensus_anchor.probability}`,
  ].join('\n');
}

export function buildUserPrompt(input: PConsensusAnalystInput): string {
  const groundingBlock = input.grounding ? renderWorldStateGrounding(input.grounding) + '\n' : '';
  return `${groundingBlock}TICKER: ${input.ticker}

TICKER DATA (price + meta):
${tickerExcerpt(input.ticker_data)}

MOMENTUM SIGNALS (code-derived from p.0.prior):
${momentumExcerpt(input.momentum)}

MARKET ANCHORS:
${anchorBlock(input.market_anchor)}

TICKER NEWS (last 48h, deduped + ranked):
${renderNewsBlock(input.news ?? [])}

REQUEST: emit_p_consensus_analyst

Emit a SINGLE JSON object matching the schema. Predict THIS TICKER's directional move UNDER THE CONSENSUS BOUNDARY SCENARIO. Cite ticker payload + momentum + consensus basis in mechanism.`;
}
